import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

const SECTIONS = [
    { id: "hero", label: "Hero" },
    { id: "pricing", label: "Pricing" },
    { id: "faq", label: "FAQ" },
];

const inputCls =
    "w-full border-2 border-ink/20 bg-white px-4 py-2.5 text-ink focus:border-coral focus:outline-none";

export default function AdminSiteContent() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [active, setActive] = useState("hero");
    const [content, setContent] = useState({});
    const [loadingSection, setLoadingSection] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            nav("/admin/login");
            return;
        }
        loadSection(active);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loading, active]);

    async function loadSection(section) {
        setLoadingSection(true);
        try {
            const { data } = await api.get(`/site-content/${section}`);
            setContent({ ...content, [section]: data.data });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load");
        } finally {
            setLoadingSection(false);
        }
    }

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/admin/site-content/${active}`, { data: content[active] });
            toast.success("Saved — refresh the homepage to see it live");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
        } finally {
            setSaving(false);
        }
    }

    function patch(patchObj) {
        setContent({ ...content, [active]: { ...content[active], ...patchObj } });
    }

    const data = content[active];

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="admin-site-content-page">
            <header className="border-b-2 border-ink/10 bg-white">
                <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-5 lg:px-10">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin"
                            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60 hover:text-coral"
                            data-testid="admin-site-content-back"
                        >
                            <ArrowLeft className="h-4 w-4" /> Dashboard
                        </Link>
                        <span className="text-ink/25">/</span>
                        <span className="font-display text-lg font-black uppercase tracking-tight text-ink">
                            Site content
                        </span>
                    </div>
                    <button
                        onClick={save}
                        disabled={saving || loadingSection}
                        className="inline-flex items-center gap-2 border-2 border-coral bg-coral px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:-translate-y-0.5 disabled:opacity-60"
                        data-testid="admin-site-content-save"
                    >
                        {saving ? (
                            <>Saving… <Loader2 className="h-4 w-4 animate-spin" /></>
                        ) : (
                            <><Save className="h-4 w-4" /> Save {SECTIONS.find((s) => s.id === active)?.label}</>
                        )}
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-[1100px] px-5 py-8 lg:px-10">
                {/* Tabs */}
                <div className="mb-8 inline-flex border-2 border-ink/20 bg-white p-1" data-testid="admin-site-content-tabs">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => setActive(s.id)}
                            className={`px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-all ${
                                active === s.id ? "bg-coral text-white" : "text-ink/70 hover:text-ink"
                            }`}
                            data-testid={`admin-site-content-tab-${s.id}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>

                {loadingSection || !data ? (
                    <div className="text-sm uppercase tracking-widest text-ink/45">Loading…</div>
                ) : active === "hero" ? (
                    <HeroEditor data={data} patch={patch} />
                ) : active === "pricing" ? (
                    <PricingEditor data={data} patch={patch} />
                ) : (
                    <FaqEditor data={data} patch={patch} />
                )}
            </main>
        </div>
    );
}

// ------- Hero editor -------
function HeroEditor({ data, patch }) {
    return (
        <div className="border-2 border-ink/15 bg-white p-6 lg:p-9">
            <h3 className="mb-6 font-display text-2xl font-black uppercase tracking-tight">Hero</h3>
            <Field label="Headline · line 1">
                <input value={data.headline_line1 || ""} onChange={(e) => patch({ headline_line1: e.target.value })} className={inputCls} data-testid="hero-line1" />
            </Field>
            <Field label="Headline · line 2">
                <input value={data.headline_line2 || ""} onChange={(e) => patch({ headline_line2: e.target.value })} className={inputCls} data-testid="hero-line2" />
            </Field>
            <div className="grid gap-5 md:grid-cols-2">
                <Field label="Line 3 · prefix (e.g. 'actually')">
                    <input value={data.headline_line3_prefix || ""} onChange={(e) => patch({ headline_line3_prefix: e.target.value })} className={inputCls} data-testid="hero-line3-prefix" />
                </Field>
                <Field label="Line 3 · highlight (coral, e.g. 'simple')">
                    <input value={data.headline_line3_highlight || ""} onChange={(e) => patch({ headline_line3_highlight: e.target.value })} className={inputCls} data-testid="hero-line3-highlight" />
                </Field>
            </div>
            <Field label="Subheadline">
                <textarea rows={4} value={data.subhead || ""} onChange={(e) => patch({ subhead: e.target.value })} className={inputCls} data-testid="hero-subhead" />
            </Field>
            <Field label="Secondary CTA text (e.g. 'Or explore our 3 packages ↓')">
                <input value={data.cta_secondary || ""} onChange={(e) => patch({ cta_secondary: e.target.value })} className={inputCls} data-testid="hero-cta-secondary" />
            </Field>
        </div>
    );
}

// ------- Pricing editor -------
function PricingEditor({ data, patch }) {
    const packages = data.packages || [];

    function updatePkg(i, patchObj) {
        patch({ packages: packages.map((p, idx) => (idx === i ? { ...p, ...patchObj } : p)) });
    }
    function updateBullet(i, bIdx, val) {
        const pkg = packages[i];
        const bullets = pkg.bullets.map((b, j) => (j === bIdx ? val : b));
        updatePkg(i, { bullets });
    }
    function addBullet(i) {
        updatePkg(i, { bullets: [...(packages[i].bullets || []), ""] });
    }
    function removeBullet(i, bIdx) {
        updatePkg(i, { bullets: packages[i].bullets.filter((_, j) => j !== bIdx) });
    }

    return (
        <div className="space-y-6">
            <div className="border-2 border-ink/15 bg-white p-6 lg:p-9">
                <h3 className="mb-5 font-display text-2xl font-black uppercase tracking-tight">Pricing section</h3>
                <div className="grid gap-5 md:grid-cols-2">
                    <Field label="Small label (e.g. '// Packages')">
                        <input value={data.label || ""} onChange={(e) => patch({ label: e.target.value })} className={inputCls} data-testid="pricing-label" />
                    </Field>
                    <Field label="Section title · prefix">
                        <input value={data.title_prefix || ""} onChange={(e) => patch({ title_prefix: e.target.value })} className={inputCls} data-testid="pricing-title-prefix" />
                    </Field>
                </div>
                <Field label="Section title · highlight (coral italic)">
                    <input value={data.title_highlight || ""} onChange={(e) => patch({ title_highlight: e.target.value })} className={inputCls} data-testid="pricing-title-highlight" />
                </Field>
                <Field label="Intro paragraph">
                    <textarea rows={3} value={data.intro || ""} onChange={(e) => patch({ intro: e.target.value })} className={inputCls} data-testid="pricing-intro" />
                </Field>
                <Field label="Footnote (below the pricing cards)">
                    <input value={data.footnote || ""} onChange={(e) => patch({ footnote: e.target.value })} className={inputCls} data-testid="pricing-footnote" />
                </Field>
            </div>

            {packages.map((pkg, i) => (
                <div key={pkg.id || i} className="border-2 border-ink/15 bg-white p-6 lg:p-9" data-testid={`pricing-pkg-${pkg.id || i}`}>
                    <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                        Package · {pkg.id || `#${i + 1}`}
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field label="Name (e.g. Start / Build / Scale)">
                            <input value={pkg.name || ""} onChange={(e) => updatePkg(i, { name: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Price (NZD, no $)">
                            <input type="number" value={pkg.price ?? 0} onChange={(e) => updatePkg(i, { price: Number(e.target.value) || 0 })} className={inputCls} />
                        </Field>
                    </div>
                    <Field label="Tagline">
                        <input value={pkg.tagline || ""} onChange={(e) => updatePkg(i, { tagline: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Bullet points">
                        <div className="space-y-2">
                            {(pkg.bullets || []).map((b, bIdx) => (
                                <div key={bIdx} className="flex gap-2">
                                    <input value={b} onChange={(e) => updateBullet(i, bIdx, e.target.value)} className={`${inputCls} flex-1`} />
                                    <button type="button" onClick={() => removeBullet(i, bIdx)} className="grid h-10 w-10 place-items-center border-2 border-ink/20 text-ink/55 hover:border-coral hover:text-coral">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={() => addBullet(i)} className="inline-flex items-center gap-2 border-2 border-dashed border-ink/25 px-3 py-2 text-xs font-bold uppercase tracking-wider text-ink/70 hover:border-coral hover:text-coral">
                                <Plus className="h-3.5 w-3.5" /> Add bullet
                            </button>
                        </div>
                    </Field>
                </div>
            ))}
        </div>
    );
}

// ------- FAQ editor -------
function FaqEditor({ data, patch }) {
    const items = data.items || [];
    function updateItem(i, patchObj) {
        patch({ items: items.map((q, idx) => (idx === i ? { ...q, ...patchObj } : q)) });
    }
    function addItem() {
        patch({ items: [...items, { q: "", a: "" }] });
    }
    function removeItem(i) {
        patch({ items: items.filter((_, idx) => idx !== i) });
    }

    return (
        <div className="space-y-6">
            <div className="border-2 border-ink/15 bg-white p-6 lg:p-9">
                <h3 className="mb-5 font-display text-2xl font-black uppercase tracking-tight">FAQ section</h3>
                <div className="grid gap-5 md:grid-cols-3">
                    <Field label="Small label">
                        <input value={data.label || ""} onChange={(e) => patch({ label: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Title · prefix">
                        <input value={data.title_prefix || ""} onChange={(e) => patch({ title_prefix: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Title · highlight (coral italic)">
                        <input value={data.title_highlight || ""} onChange={(e) => patch({ title_highlight: e.target.value })} className={inputCls} />
                    </Field>
                </div>
            </div>

            {items.map((q, i) => (
                <div key={i} className="border-2 border-ink/15 bg-white p-6 lg:p-9" data-testid={`faq-item-${i}`}>
                    <div className="mb-4 flex items-center justify-between">
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-coral">Question · {i + 1}</div>
                        <button
                            type="button"
                            onClick={() => removeItem(i)}
                            className="grid h-8 w-8 place-items-center border-2 border-ink/20 text-ink/55 hover:border-coral hover:text-coral"
                            aria-label="Remove"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                    <Field label="Question">
                        <input value={q.q || ""} onChange={(e) => updateItem(i, { q: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Answer">
                        <textarea rows={4} value={q.a || ""} onChange={(e) => updateItem(i, { a: e.target.value })} className={inputCls} />
                    </Field>
                </div>
            ))}

            <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center gap-2 border-2 border-dashed border-ink/25 px-5 py-3 text-xs font-bold uppercase tracking-wider text-ink/70 hover:border-coral hover:text-coral"
                data-testid="faq-add"
            >
                <Plus className="h-4 w-4" /> Add question
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="mb-5 block">
            <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60">
                {label}
            </span>
            {children}
        </label>
    );
}
